import { getConfig } from '@superbet-group/web.lib.config';
import { logger } from '@superbet-group/web.lib.utilities';
import { BonusType } from '@superbet-group/web.types';
import { orderBy as _orderBy } from 'lodash';
import { ActionContext } from 'vuex';

import { IState } from '@app/store';
import { clientStorageWithExpiry } from '@core/clientStorage/clientStorageWithExpiry';
import { BonusSubCategory } from '@core/mappers/user/dtos/BalanceDto';
import {
    Bonus,
    BetlerBonus,
    isComtradeBonus,
    ComtradeBonus,
} from '@core/models/bonus/Bonus';
import BonusCategory from '@core/models/bonus/BonusCategory';
import BonusEligibility from '@core/models/bonus/BonusEligibility';
import { BonusGroups } from '@core/models/bonus/BonusGroups';
import BonusProvider from '@core/models/bonus/BonusProvider';
import { BonusStatus } from '@core/models/bonus/BonusStatus';
import { BonusUtilization } from '@core/models/bonus/BonusUtilization';
import { CashType } from '@core/models/bonus/CashType';
import {
    BetlerWelcomeBonus,
    ComtradeWelcomeBonus,
    WelcomeBonus,
    WelcomeBonusKind,
} from '@core/models/bonus/WelcomeBonus';
import { WelcomeOfferBonusType } from '@core/models/bonus/WelcomeOfferBonusType';
import { WinCreditType } from '@core/models/bonus/WinCreditType';
import { BonusBalanceData } from '@core/models/user/BonusBalanceData';
import { isNetWin } from '@shared/betSlip/helpers/betSlipBonusHelper';
import groupBy from '@utils/dataTransformation/groupBy';
import { MIN_DATE } from '@utils/date/constants';

import { ACTIVE_SPORT_BONUS_ID } from './bonusIndex';
import {
    BonusState,
    sportBonusTypes,
} from './types';
import { shellLocalConfig } from '@src/config/shell/localConfig';
import {
    isBingoBonus,
    isFreeBetBonus,
    isFreeSpinBonus,
    isSportOrFreeBetBonus,
} from '@src/app/promotionHub/bonuses/utils/bonusTypeHelpers';
import { BonusState as BonusStatePromo } from '@store/modules/ui/promotionHub/bonus/bonusTypes';

// Mockup object for bonusEligibility because Betler currently don't support bonus eligibility
// Once Betler will support bonus eligibility, this mockup object will be replaced with real data
// This is needed for showing bonus breakdown in betSlip.
const bonusEligibilityMockup: BonusEligibility = {
    name: 'BonusMockup',
    bonusId: 1,
    balance: 0,
    status: BonusStatus.Active,
    utilization: BonusUtilization.AnyBet,
    cashType: CashType.NonCashable,
    winType: WinCreditType.Total,
    eventsOdds: [],
};

export function sortChronologically(bonuses: Bonus[]): Bonus[] {
    return _orderBy(
        bonuses,
        [
            // we want to treat undefined values as lowest, placing them at the end
            (bonus) => (shellLocalConfig.bonus.isPromoRewardVisible ? bonus.activationDate : bonus.acceptanceDate) ?? MIN_DATE,
            (bonus) => isComtradeBonus(bonus) ? bonus.expirationDate : bonus.completedDate,
            'id',
        ],
        [
            'desc',
            'desc',
            'desc',
        ],
    );
}

export function sortByType(bonuses: Bonus[]): Bonus[] {
    const activeBonusPriority: Record<BonusType, number> = {
        [BonusType.Sport]: 1,
        [BonusType.Freebet]: 2,
        [BonusType.Virtuals]: 3,
        [BonusType.Casino]: 4,
        [BonusType.LiveDealer]: 4,
        [BonusType.FreeSpins]: 5,
        [BonusType.Bingo]: 5,
        [BonusType.CasinoVirtuals]: 6,
        [BonusType.Cash]: 7,
        [BonusType.HoneyToken]: 8,
    };
    return _orderBy(
        bonuses,
        (bonus) => activeBonusPriority[bonus.type],
        'asc',
    );
}

/**
 * Sets active bonuses' values to equal the current balance instead of the initially awarded amount.
 *
 * Example: The user receives a bonus worth 100 RON. They then spend 40 RON. We want to display the remaining 60 RON
 * as the bonus value, not the initial 100 RON.
 *
 * Of course, this information isn't included in the bonus API. It is instead made available through the 'getBalance'
 * API. This function fetches the correct values and updates the bonuses accordingly.
 *
 * @param activeBonuses An array of active bonuses received through the bonus API, it MUST be up to date.
 * @param dispatch Vuex's dispatch object.
 * @param rootGetters Vuex's rootGetters.
 */

export async function adjustActiveBonuses(actionContext: ActionContext<BonusState | BonusStatePromo, IState>, activeBonuses: ComtradeBonus[], shouldUpdateBalance: boolean): Promise<Bonus[]> {
    if (shouldUpdateBalance) {
        await actionContext.dispatch('ui/user/account/getBalance', { shouldCheckBonus: false }, { root: true });
    }
    adjustFreeSpinAndBonusAmount(actionContext as ActionContext<BonusState, IState>, activeBonuses);

    const activeBonusBalances: BonusBalanceData = actionContext.rootGetters['data/authentication/userBonusBalanceData'];
    const adjustedActiveBonuses = activeBonuses.map((bonus: Bonus) => ({
        ...bonus,
        /*
            The appropriate bonus balance must exist. Otherwise, the bonus would not be active. Therefore, we can
            use non-null assertion. We also know that there is exactly one-on-one correspondence between the active
            bonuses and the balance information
        */
        bonusAmount: bonus.category === BonusCategory.Active
            ? getBonusAmount(bonus, activeBonusBalances)
            : bonus.bonusAmount,
    }));

    // sort by status
    const activeBonusPriority: Record<BonusStatus, number> = {
        [BonusStatus.Active]: 1,
        [BonusStatus.AwardedExternal]: 1,
        [BonusStatus.Pending]: 2,
        [BonusStatus.Waiting]: 2,
        [BonusStatus.Redeemed]: 3,
        [BonusStatus.Declined]: 3,
        [BonusStatus.Expired]: 3,
        [BonusStatus.Lost]: 3,
    };
    return _orderBy(
        adjustedActiveBonuses,
        [
            (bonus) => activeBonusPriority[bonus.status],
            (bonus) => bonus.expirationDate ?? MIN_DATE,
        ],
        [
            'asc',
            'asc',
        ],
    );
}

export function getBonusAmount(bonus: Bonus, activeBonusBalances: BonusBalanceData): number {
    if (isComtradeBonus(bonus)) {
        return bonus.subCategory === BonusSubCategory.FreeBet ? activeBonusBalances[BonusType.Freebet].amount! : activeBonusBalances[bonus.type].amount!;
    } else {
        return activeBonusBalances[bonus.type].amount!;
    }
}

export function getBonusEligibilityAmount(bonus: BonusEligibility, activeBonusBalances: BonusBalanceData): number {
    return isNetWin(bonus) ? activeBonusBalances[BonusType.Freebet].amount! : activeBonusBalances[BonusType.Sport].amount!;
}

export function orderActiveBonuses(bonuses: BetlerBonus[]): Bonus[] {
    return _orderBy(
        bonuses,
        [
            (bonus) => bonus.priority ?? Number.MIN_SAFE_INTEGER,
            // First that expires comes first
            (bonus) => bonus.expirationDate ?? MIN_DATE,
        ],
        [
            'desc',
            'asc',
        ],
    );
}

export function getActiveSportBonusFromBalance(rootGetters: any): number | undefined {
    const activeBonusBalances: BonusBalanceData = rootGetters['data/authentication/userBonusBalanceData'];
    for (const bonus in activeBonusBalances) {
        if (isSportOrFreeBetBonus(bonus as BonusType) && activeBonusBalances[bonus as BonusType].id) {
            return activeBonusBalances[bonus as BonusType].id!;
        }
    }
}

export function getActiveBonusEligibility(bonusEligibilities: BonusEligibility[]): BonusEligibility | undefined {
    const activeSportBonusId = clientStorageWithExpiry.get<number | null>(ACTIVE_SPORT_BONUS_ID);
    return bonusEligibilities.find((eligibility) => eligibility.bonusId === activeSportBonusId && eligibility.status === BonusStatus.Active);
}

export function convertBonusDate(expirationDate: string | Date | undefined): Date | undefined {
    return typeof expirationDate === 'string' ? new Date(expirationDate) : expirationDate;
}

export function getFirstSportBonus(bonuses: BetlerBonus[]): BetlerBonus {
    if (bonuses.length === 1) {
        return bonuses[0];
    }

    const orderedBonuses = _orderBy(
        bonuses,
        [
            (bonus) => bonus.priority ?? Number.MIN_SAFE_INTEGER,
            (bonus) => bonus.activationDate ?? new Date(),
        ],
        [
            'desc',
            'asc',
        ],
    );

    return orderedBonuses[0];
}

export function getBetlerBonus(bonus: BetlerBonus, bonusBalances: BonusBalanceData): BonusEligibility {
    return {
        ...bonusEligibilityMockup,
        balance: bonusBalances[bonus.type].amount || 0,
        bonusType: bonus.type,
        moneyUsage: bonus.moneyUsage,
        winType: isFreeBetBonus(bonus.type) ? WinCreditType.Net : WinCreditType.Total,
        expirationDate: bonus.expirationDate,
        minTicketOdd: bonus.bonusConditions?.minTotalOdd,
        minEventOdd: bonus.bonusConditions?.minEventOdd,
        utilizationEvents: bonus.bonusConditions?.events.map((event) => event.id),
        utilization: bonus.bonusConditions?.events && bonus.bonusConditions?.events.length > 0 ? BonusUtilization.Custom : BonusUtilization.AnyBet,
    };
}

/**
 * Get all sport bonuses that have some amount left.
 */
export function getSportBonuses(bonuses: BetlerBonus[]): BetlerBonus[] {
    return bonuses.filter((bonus) => sportBonusTypes.has(bonus.type) && bonus.bonusPotentialWin > 0);
}

/**
 * Bonus eligibility on Betler can be available if there is at least one active sport bonus
 */
export function isBetlerBonusEligibilityAvailable(activeBonuses: Bonus[]): boolean {
    return activeBonuses.length > 0;
}

export function getNoWelcomeBonusOption(): WelcomeBonus {
    switch (getConfig().countrySpecific.user.bonus.bonusProvider) {
        case BonusProvider.Comtrade:
            return {
                kind: WelcomeBonusKind.Comtrade,
                id: 142,
                title: 'bonus.welcome_offer.no_bonus_title',
                description: 'bonus.welcome_offer.reject_all_bonuses',
                type: WelcomeOfferBonusType.NoBonus,
                code: WelcomeOfferBonusType.NoBonus,
                staticUrl: null,
            } as ComtradeWelcomeBonus;
        case BonusProvider.Betler:
            return {
                kind: WelcomeBonusKind.Betler,
                id: WelcomeOfferBonusType.NoBonus,
                title: 'bonus_comms.common.bonus_title',
                description: 'bonus.welcome_offer.decline_description',
                image: '/static/img/bonus/welcome-bonus__decline.png',
                type: WelcomeOfferBonusType.NoBonus,
                buttonText: 'bonus.welcome_offer.decline_bonus_button',
            } as BetlerWelcomeBonus;
        default:
            logger.error(new Error('Error getting No Welcome bonus option'));
            return {} as WelcomeBonus;
    }
}

export function sortByTypeAndGroupByCategory(bonuses: Bonus[]): BonusGroups {
    const sortedBonuses = sortByType(bonuses);
    const statusGroups = groupBy(sortedBonuses, 'category');

    const getBonuses = (category: BonusCategory) => statusGroups.get(category) || [];

    const wagerableBonuses = getBonuses(BonusCategory.AvailableToWager);
    const unwagerableBonuses = getBonuses(BonusCategory.UnavailableToWager);
    const crtieriaRequiredBonuses = getBonuses(BonusCategory.CriteriaRequired);

    const historyBonuses = sortChronologically(getBonuses(BonusCategory.History));

    const activeBonuses = getBonuses(BonusCategory.Active);

    return {
        activeBonuses,
        historyBonuses,
        pendingBonuses: wagerableBonuses.concat(unwagerableBonuses, crtieriaRequiredBonuses),
    };
}

/**
 FreeSpins in iCore are tracked as pending so we need to update free spins amount,
 Bingo bonuses are new type of bonuses that isn't returned in getPlayerBalance endpoint, so we need to update it manually
 */
function adjustFreeSpinAndBonusAmount(actionContext: ActionContext<BonusState, IState>, activeBonuses: ComtradeBonus[]) {
    const freeSpinsAmount = activeBonuses.reduce((accumulator, bonus) => {
        return isFreeSpinBonus(bonus.type) && bonus.bonusItemsAwarded
            ? accumulator + bonus.bonusItemsAwarded
            : accumulator;
    }, 0);

    const bingoAmount = activeBonuses.reduce((accumulator, bonus) => {
        return isBingoBonus(bonus.type) && bonus.bonusItemsAwarded
            ? accumulator + bonus.bonusItemsAwarded
            : accumulator;
    }, 0);

    const activeBonusBalances: BonusBalanceData = actionContext.rootGetters['data/authentication/userBonusBalanceData'];
    activeBonusBalances.FreeSpins.amount = freeSpinsAmount;
    activeBonusBalances.Bingo.amount = bingoAmount;

    actionContext.dispatch(
        'data/authentication/setUserData',
        {
            key: 'userBonusBalanceData',
            value: activeBonusBalances,
        },
        { root: true },
    );
}
